import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  CardActions,
  ToggleButtonGroup,
  ToggleButton,
  MenuItem,
  TextField,
  IconButton,
  FormControlLabel,
  Checkbox,
  InputLabel,
} from "@mui/material";
import {
  Calendar,
  Link as LinkIcon,
  RefreshCw,
  Layers,
  User,
  X,
  Check,
} from "lucide-react";
import axios from "axios";
import moment from "moment";
import * as constants from "../../../../utils/constants";
import { OutlookCalendar } from "../../../Calendar";
import UserInfo from "../../../../utils/Authorization/UserInfo";
import ROLES from "../../../../utils/Authorization/Roles";

const API_URL = `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}`;

const OutlookCalendarPage = () => {
  const userInfo = UserInfo();
  const hasUserInfo = userInfo && Object.keys(userInfo).length > 0;
  const isSuperAdmin =
    hasUserInfo && userInfo?.role?.includes(ROLES.SUPER_ADMIN);
  const currentUserId = hasUserInfo ? userInfo?.pk : null;

  const [searchParams, setSearchParams] = useSearchParams();
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [selectedView, setSelectedView] = useState("all");

  const [adminUsers, setAdminUsers] = useState([]);
  const [adminUsersLoading, setAdminUsersLoading] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);

  const [showCommonSlots, setShowCommonSlots] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [commonFreeSlots, setCommonFreeSlots] = useState([]);
  const [commonSlotsLoading, setCommonSlotsLoading] = useState(false);
  const [commonSlotStats, setCommonSlotStats] = useState({
    dayCount: 0,
    slotCount: 0,
  });
  const [timeRangeStart, setTimeRangeStart] = useState(9);
  const [timeRangeEnd, setTimeRangeEnd] = useState(21);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("authToken");
    return {
      Authorization: `Token ${token}`,
      "Content-Type": "application/json",
    };
  }, []);

  const checkConnectionStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${API_URL}/api/v1/outlook/auth/status/`,
        { headers: getAuthHeaders() },
      );
      setConnectionStatus(response.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setConnectionStatus({ connected: false });
      } else {
        setError(
          err.response?.data?.error || "Failed to check connection status",
        );
      }
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  const initiateConnection = useCallback(async () => {
    setConnecting(true);
    setError(null);
    try {
      const response = await axios.post(
        `${API_URL}/api/v1/outlook/auth/initiate/`,
        {},
        { headers: getAuthHeaders() },
      );

      if (response.data.authorization_url) {
        window.location.href = response.data.authorization_url;
      }
    } catch (err) {
      setError(err.response?.data?.error || "Failed to initiate connection");
      setConnecting(false);
    }
  }, [getAuthHeaders]);

  const fetchAdminUsers = useCallback(async () => {
    setAdminUsersLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/v1/outlook/admin/users-with-connections/`,
        { headers: getAuthHeaders() },
      );
      const users = response.data.users || [];
      setAdminUsers(users);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to fetch users");
    } finally {
      setAdminUsersLoading(false);
    }
  }, [getAuthHeaders]);

  const fetchCommonFreeSlots = useCallback(
    async (userIds) => {
      if (userIds.length < 2) {
        setCommonFreeSlots([]);
        setCommonSlotStats({ dayCount: 0, slotCount: 0 });
        return;
      }

      setCommonSlotsLoading(true);
      setError(null);

      try {
        const userTimezone = moment.tz.guess();

        const now = moment().tz(userTimezone);
        const startDate = now
          .clone()
          .startOf("month")
          .startOf("day")
          .format("YYYY-MM-DDTHH:mm:ss");
        const endDate = now
          .clone()
          .endOf("month")
          .endOf("day")
          .format("YYYY-MM-DDTHH:mm:ss");

        const response = await axios.post(
          `${API_URL}/api/v1/outlook/common-free-slots/`,
          {
            user_ids: userIds,
            start_time: startDate,
            end_time: endDate,
            interval: 30,
            timezone: userTimezone,
          },
          { headers: getAuthHeaders() },
        );

        if (response.data.success) {
          const daysData = response.data.days || {};
          const dayCount =
            response.data.day_count || Object.keys(daysData).length;
          const totalSlotCount = response.data.slot_count || 0;

          setCommonSlotStats({
            dayCount,
            slotCount: totalSlotCount,
          });

          const filterStartMinutes = timeRangeStart * 60;
          const filterEndMinutes = timeRangeEnd * 60;

          const convertedSlots = [];
          const seenSlots = new Set();

          Object.entries(daysData).forEach(([dateKey, daySlots]) => {
            if (!daySlots || daySlots.length === 0) return;

            daySlots.forEach((slot) => {
              const slotStartUTC = moment.utc(slot.start_time);
              const slotEndUTC = moment.utc(slot.end_time);

              const slotStart = slotStartUTC.clone().tz(userTimezone);
              const slotEnd = slotEndUTC.clone().tz(userTimezone);

              let currentDate = slotStart.clone();
              const endDate = slotEnd.clone();

              let dayCounter = 0;
              const maxDays = 100;

              const startDay = slotStart.clone().startOf("day");
              const endDay = slotEnd.clone().startOf("day");

              currentDate = startDay.clone();

              while (
                currentDate.isSameOrBefore(endDay, "day") &&
                dayCounter < maxDays
              ) {
                dayCounter++;
                const dayDate = currentDate.clone().format("YYYY-MM-DD");
                const dayStart = currentDate.clone().startOf("day");
                const dayEnd = currentDate.clone().endOf("day");

                let chunkStart = slotStart.isAfter(dayStart)
                  ? slotStart.clone()
                  : dayStart.clone();
                let chunkEnd = slotEnd.isBefore(dayEnd)
                  ? slotEnd.clone()
                  : dayEnd.clone();

                if (currentDate.isSame(startDay, "day")) {
                  chunkStart = slotStart.isAfter(dayStart)
                    ? slotStart.clone()
                    : dayStart.clone();
                }
                if (currentDate.isSame(endDay, "day")) {
                  chunkEnd = slotEnd.isBefore(dayEnd)
                    ? slotEnd.clone()
                    : dayEnd.clone();
                }
                if (
                  currentDate.isAfter(startDay, "day") &&
                  currentDate.isBefore(endDay, "day")
                ) {
                  chunkStart = dayStart.clone();
                  chunkEnd = dayEnd.clone();
                }

                if (!chunkStart.isBefore(chunkEnd)) {
                  currentDate.add(1, "day");
                  continue;
                }

                const chunkStartMinutes =
                  chunkStart.hour() * 60 + chunkStart.minute();
                const chunkEndMinutes =
                  chunkEnd.hour() * 60 + chunkEnd.minute();

                if (chunkEndMinutes <= filterStartMinutes) {
                  currentDate.add(1, "day");
                  continue;
                }
                if (chunkStartMinutes >= filterEndMinutes) {
                  currentDate.add(1, "day");
                  continue;
                }

                let adjustedStart = chunkStart.clone();
                let adjustedEnd = chunkEnd.clone();

                if (chunkStartMinutes < filterStartMinutes) {
                  adjustedStart = currentDate
                    .clone()
                    .startOf("day")
                    .set({ hour: timeRangeStart, minute: 0, second: 0 });
                }
                if (chunkEndMinutes > filterEndMinutes) {
                  adjustedEnd = currentDate
                    .clone()
                    .startOf("day")
                    .set({ hour: timeRangeEnd, minute: 0, second: 0 });
                }

                if (adjustedStart.isBefore(adjustedEnd)) {
                  const slotId = `${dayDate}-${adjustedStart.format("HHmm")}-${adjustedEnd.format("HHmm")}`;

                  if (!seenSlots.has(slotId)) {
                    seenSlots.add(slotId);
                    convertedSlots.push({
                      id: slotId,
                      title: `${adjustedStart.format("HH:mm")}-${adjustedEnd.format("HH:mm")}`,
                      start: adjustedStart.toDate(),
                      end: adjustedEnd.toDate(),
                      isCommonSlot: true,
                      date: dayDate,
                    });
                  }
                }

                currentDate.add(1, "day");
              }
            });
          });

          convertedSlots.sort((a, b) => {
            const dateCompare = a.date.localeCompare(b.date);
            if (dateCompare !== 0) return dateCompare;
            return new Date(a.start) - new Date(b.start);
          });

          setCommonFreeSlots(convertedSlots);
        }
      } catch (err) {
        setError(
          err.response?.data?.error || "Failed to fetch common free slots",
        );
        setCommonFreeSlots([]);
        setCommonSlotStats({ dayCount: 0, slotCount: 0 });
      } finally {
        setCommonSlotsLoading(false);
      }
    },
    [getAuthHeaders, timeRangeStart, timeRangeEnd],
  );

  const handleUserChange = useCallback(
    (userId) => {
      if (!userId || userId === String(currentUserId)) {
        setSelectedUserId(null);
      } else {
        setSelectedUserId(userId);
      }
    },
    [currentUserId],
  );

  const handleClearUser = useCallback(() => {
    setSelectedUserId(null);
  }, []);

  const handleCommonSlotsToggle = useCallback((event) => {
    const checked = event.target.checked;
    setShowCommonSlots(checked);
    if (!checked) {
      setSelectedUserIds([]);
      setSelectedUserId(null);
      setCommonFreeSlots([]);
      setCommonSlotStats({ dayCount: 0, slotCount: 0 });
    }
  }, []);

  const getMultiSelectValue = useCallback(() => {
    if (!showCommonSlots) return selectedUserId || String(currentUserId) || "";
    return selectedUserIds;
  }, [showCommonSlots, selectedUserId, currentUserId, selectedUserIds]);

  useEffect(() => {
    if (showCommonSlots && selectedUserIds.length >= 2) {
      fetchCommonFreeSlots(selectedUserIds);
    } else {
      setCommonFreeSlots([]);
      setCommonSlotStats({ dayCount: 0, slotCount: 0 });
    }
  }, [
    showCommonSlots,
    selectedUserIds,
    timeRangeStart,
    timeRangeEnd,
    fetchCommonFreeSlots,
  ]);

  useEffect(() => {
    checkConnectionStatus();
  }, [checkConnectionStatus]);

  useEffect(() => {
    if (isSuperAdmin) {
      fetchAdminUsers();
    }
  }, [isSuperAdmin, fetchAdminUsers]);

  if (loading || !hasUserInfo) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          minHeight: 400,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const allConnections = connectionStatus?.connections || [];
  const defaultConnection = connectionStatus?.default_connection;
  const isOwnConnected = connectionStatus?.connected;

  const isOwnMode = !selectedUserId;
  const isAdminMode = selectedUserId && adminUsers.length > 0;

  const selectedUser = isAdminMode
    ? adminUsers.find((u) => String(u.id) === String(selectedUserId))
    : null;

  const getCalendarProps = () => {
    if (selectedView === "all" && allConnections.length > 0) {
      return {
        connections: allConnections.map((c) => ({
          id: String(c.id),
          email: c.email,
        })),
      };
    }
    const conn = allConnections.find((c) => String(c.id) === selectedView);
    if (conn) {
      return { connectionId: String(conn.id) };
    }
    return {
      connectionId: defaultConnection?.id ? String(defaultConnection.id) : null,
    };
  };

  const calendarProps = getCalendarProps();
  const selectedConn =
    selectedView !== "all"
      ? allConnections.find((c) => String(c.id) === selectedView)
      : null;

  const adminTargetUserId =
    isAdminMode && selectedUser ? String(selectedUser.id) : null;

  const isCommonSlotsMode = showCommonSlots && selectedUserIds.length >= 2;

  // Only block non-super-admins without connections, and allow super admins to see common slots
  if (!isSuperAdmin && !isOwnConnected && !isCommonSlotsMode) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          minHeight: 400,
          p: 3,
        }}
      >
        <Card sx={{ maxWidth: 450, textAlign: "center" }}>
          <CardContent>
            <Calendar
              size={64}
              style={{ marginBottom: 16, color: "#1976d2" }}
            />
            <Typography variant="h5" gutterBottom>
              Connect Microsoft Outlook
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Connect your Microsoft Outlook account to access your calendar,
              create events, and manage your schedule directly from this
              dashboard.
            </Typography>
            {error && (
              <Alert severity="error" sx={{ mb: 2, textAlign: "left" }}>
                {error}
              </Alert>
            )}
          </CardContent>
          <CardActions sx={{ justifyContent: "center", pb: 3 }}>
            <Button
              variant="contained"
              size="large"
              startIcon={
                connecting ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <LinkIcon size={20} />
                )
              }
              onClick={initiateConnection}
              disabled={connecting}
            >
              {connecting ? "Connecting..." : "Connect Outlook"}
            </Button>
          </CardActions>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: "calc(100vh - 150px)",
        display: "flex",
        flexDirection: "column",
        p: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography variant="h5">Outlook Calendar</Typography>
          {!isSuperAdmin && (
            <Typography variant="body2" color="text.secondary">
              {selectedView === "all"
                ? `${allConnections.length} account${allConnections.length !== 1 ? "s" : ""} connected`
                : `Viewing ${selectedConn?.email || ""}`}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {isSuperAdmin && (
            <>
              {showCommonSlots && (
                <>
                  <TextField
                    select
                    label="Start"
                    size="small"
                    value={timeRangeStart}
                    onChange={(e) =>
                      setTimeRangeStart(parseInt(e.target.value))
                    }
                    sx={{ width: 100, "& .MuiInputBase-root": { height: 32 } }}
                    InputLabelProps={{ shrink: true }}
                  >
                    {Array.from({ length: 24 }, (_, i) => (
                      <MenuItem key={i} value={i}>
                        {String(i).padStart(2, "0")}:00
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    label="End"
                    size="small"
                    value={timeRangeEnd}
                    onChange={(e) => setTimeRangeEnd(parseInt(e.target.value))}
                    sx={{ width: 100, "& .MuiInputBase-root": { height: 32 } }}
                    InputLabelProps={{ shrink: true }}
                  >
                    {Array.from({ length: 24 }, (_, i) => (
                      <MenuItem key={i} value={i}>
                        {String(i).padStart(2, "0")}:00
                      </MenuItem>
                    ))}
                  </TextField>
                </>
              )}

              <FormControlLabel
                control={
                  <Checkbox
                    checked={showCommonSlots}
                    onChange={handleCommonSlotsToggle}
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2">Free common time</Typography>
                }
                sx={{ mr: 1 }}
              />

              <TextField
                select
                size="small"
                multiple={showCommonSlots}
                value={getMultiSelectValue()}
                onChange={
                  showCommonSlots
                    ? undefined
                    : (e) => handleUserChange(e.target.value)
                }
                sx={{
                  minWidth: 250,
                  width: 250,
                  "& .MuiSelect-select": { py: 0.75 },
                  "& .MuiInputBase-root": { height: 32 },
                }}
                disabled={adminUsersLoading || commonSlotsLoading}
                SelectProps={{
                  displayEmpty: true,
                  multiple: showCommonSlots,
                  renderValue: showCommonSlots
                    ? (selected) =>
                        selected.length > 0
                          ? `${selected.length} users selected`
                          : "Select users"
                    : undefined,
                  MenuProps: {
                    PaperProps: {
                      sx: { width: 250 },
                    },
                  },
                }}
                InputProps={
                  showCommonSlots && selectedUserIds.length > 0
                    ? {
                        endAdornment: (
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedUserIds([]);
                              setSelectedUserId(null);
                              setShowCommonSlots(false);
                              setCommonFreeSlots([]);
                            }}
                          >
                            <X size={16} />
                          </IconButton>
                        ),
                      }
                    : !showCommonSlots && {
                        endAdornment: adminUsersLoading ? (
                          <CircularProgress size={16} />
                        ) : selectedUserId ? (
                          <IconButton size="small" onClick={handleClearUser}>
                            <X size={16} />
                          </IconButton>
                        ) : null,
                      }
                }
              >
                {!showCommonSlots && (
                  <MenuItem value={String(currentUserId) || ""}>
                    {userInfo?.first_name && userInfo?.last_name
                      ? `${userInfo.first_name} ${userInfo.last_name}`
                      : userInfo?.email || "My Calendar"}
                  </MenuItem>
                )}
                {showCommonSlots && adminUsers.length > 0 && (
                  <MenuItem
                    value="select_all"
                    onClick={() => {
                      if (
                        selectedUserIds.length === adminUsers.length &&
                        adminUsers.length > 0
                      ) {
                        setSelectedUserIds([]);
                      } else {
                        setSelectedUserIds(adminUsers.map((u) => String(u.id)));
                      }
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <Checkbox
                        checked={
                          selectedUserIds.length === adminUsers.length &&
                          adminUsers.length > 0
                        }
                        size="small"
                        sx={{ mr: 1 }}
                      />
                      <Typography sx={{ flex: 1, fontWeight: 600 }}>
                        Select All
                      </Typography>
                    </Box>
                  </MenuItem>
                )}
                {adminUsers.map((u) => (
                  <MenuItem
                    key={u.id}
                    value={u.id}
                    onClick={() => {
                      const userIdStr = String(u.id);
                      if (selectedUserIds.includes(userIdStr)) {
                        setSelectedUserIds(
                          selectedUserIds.filter((id) => id !== userIdStr),
                        );
                      } else {
                        setSelectedUserIds([...selectedUserIds, userIdStr]);
                      }
                    }}
                  >
                    {showCommonSlots ? (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          width: "100%",
                        }}
                      >
                        <Checkbox
                          checked={selectedUserIds.includes(String(u.id))}
                          size="small"
                          sx={{ mr: 1 }}
                        />
                        <Typography sx={{ flex: 1 }}>
                          {u.first_name && u.last_name
                            ? `${u.first_name} ${u.last_name}`
                            : u.email}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography>
                        {u.first_name && u.last_name
                          ? `${u.first_name} ${u.last_name}`
                          : u.email}
                      </Typography>
                    )}
                  </MenuItem>
                ))}
              </TextField>
            </>
          )}

          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshCw size={16} />}
            onClick={() => {
              checkConnectionStatus();
              if (isSuperAdmin) {
                fetchAdminUsers();
                if (selectedUserIds.length >= 2) {
                  fetchCommonFreeSlots(selectedUserIds);
                }
              }
            }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {(!isSuperAdmin || (isSuperAdmin && isOwnMode && !showCommonSlots)) &&
        allConnections.length > 1 && (
          <Box sx={{ mb: 1 }}>
            <ToggleButtonGroup
              value={selectedView}
              exclusive
              onChange={(e, val) => {
                if (val !== null) setSelectedView(val);
              }}
              size="small"
              sx={{
                "& .MuiToggleButton-root": {
                  textTransform: "none",
                  px: 2,
                  py: 0.5,
                },
              }}
            >
              <ToggleButton value="all">
                <Layers size={16} style={{ marginRight: 6 }} />
                All Calendars
              </ToggleButton>
              {allConnections.map((conn) => (
                <ToggleButton key={conn.id} value={String(conn.id)}>
                  <User size={16} style={{ marginRight: 6 }} />
                  {conn.email}
                  {conn.is_default && (
                    <Typography
                      component="span"
                      sx={{
                        ml: 0.5,
                        fontSize: "0.65rem",
                        color: "primary.main",
                        fontWeight: 600,
                      }}
                    >
                      (Default)
                    </Typography>
                  )}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
        )}

      {error && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {isCommonSlotsMode && commonFreeSlots.length > 0 && (
        <Box sx={{ mb: 1, display: "flex", gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Days with common free time:{" "}
            <strong>{commonSlotStats.dayCount}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Total common slots: <strong>{commonSlotStats.slotCount}</strong>
          </Typography>
        </Box>
      )}

      <Paper sx={{ flex: 1, overflow: "hidden" }}>
        {isCommonSlotsMode ? (
          <OutlookCalendar
            key="common-slots"
            events={commonFreeSlots}
            height="100%"
          />
        ) : isSuperAdmin && isOwnMode && !isOwnConnected ? (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Card sx={{ maxWidth: 450, textAlign: "center" }}>
              <CardContent>
                <Calendar
                  size={64}
                  style={{ marginBottom: 16, color: "#1976d2" }}
                />
                <Typography variant="h5" gutterBottom>
                  Connect Microsoft Outlook
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Connect your Microsoft Outlook account to access your
                  calendar, create events, and manage your schedule directly
                  from this dashboard.
                </Typography>
              </CardContent>
              <CardActions sx={{ justifyContent: "center", pb: 3 }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={
                    connecting ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <LinkIcon size={20} />
                    )
                  }
                  onClick={initiateConnection}
                  disabled={connecting}
                >
                  {connecting ? "Connecting..." : "Connect Outlook"}
                </Button>
              </CardActions>
            </Card>
          </Box>
        ) : isAdminMode && selectedUser ? (
          <OutlookCalendar
            key={`admin-${adminTargetUserId}`}
            targetUserId={adminTargetUserId}
            height="100%"
          />
        ) : (
          <OutlookCalendar
            key={selectedView}
            {...calendarProps}
            height="100%"
          />
        )}
      </Paper>
    </Box>
  );
};

export default OutlookCalendarPage;
